import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormValidation from "../../../Common/FormValidation";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import { Calendar } from "primereact/calendar";
import { ProgressSpinner } from "primereact/progressspinner";

const TransactionEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [inputs, setInputs] = useState({});
  const [error, setError] = useState({});
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankBalance, setBankBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState("");

  useEffect(() => {
    Promise.all([
      Http.get(`/accounts/fund-transfers/${id}`),
      Http.get("/accounts/banks")
    ])
      .then(([transactionRes, banksRes]) => {
        const transactionData = transactionRes.data;
        if (transactionData.date) {
          transactionData.date = new Date(transactionData.date);
        }
        setInputs(transactionData);
        setBanks(banksRes.data);
        
        // Fetch bank balance for the current account
        if (transactionData.account_id) {
          fetchBankBalance(transactionData.account_id);
        }
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const fetchBankBalance = (bankId) => {
    if (!bankId) {
      setBankBalance(null);
      setBalanceError("");
      return;
    }

    setLoadingBalance(true);
    setBalanceError("");

    Http.get(`/accounts/get-single-bank-ledger-summary/${bankId}`)
      .then((res) => {
        if (res.data.success) {
          setBankBalance(res.data.data);
        } else {
          setBalanceError("Failed to fetch bank balance");
        }
      })
      .catch((error) => {
        console.error("Balance fetch error:", error);
        setBalanceError("Error fetching bank balance");
        setBankBalance(null);
      })
      .finally(() => {
        setLoadingBalance(false);
      });
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));

    // Fetch bank balance when account is selected
    if (name === "account_id") {
      fetchBankBalance(value);
    }
  };

  const handleDateChange = (e) => {
    setInputs((values) => ({ ...values, date: e.value }));
  };

  const formSubmit = () => {
    Http.put(`/accounts/fund-transfers/${id}`, inputs)
      .then((res) => {
        navigate("/accounts-for-supplychain/transaction/list", {
          state: { message: res.data.message, type: "success" },
        });
      })
      .catch(function (e) {
        setError(e.response.data.errors);
      });
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Edit Transaction</h5>
        </div>
        <div className="am-pagebody">
          <div className="card pd-20 pd-sm-40">
            <h6 className="card-body-title mg-sm-b-30">Transaction Information</h6>
            <div className="row">
              <div className="col-lg">
                <FormValidation error={error} />
              </div>
            </div>
            
            {loading ? (
              <ProgressSpinner />
            ) : (
              <>
                <div className="row">
                  <div className="col-lg">
                    <label>Date <span className="text-danger">*</span></label>
                    <Calendar
                      value={inputs.date}
                      onChange={handleDateChange}
                      dateFormat="dd/mm/yy"
                      className="w-100"
                    />
                  </div>
                  <div className="col-lg">
                    <label>Reference</label>
                    <input
                      type="text"
                      className="form-control"
                      name="reference"
                      value={inputs.reference || ""}
                      onChange={handleChange}
                      placeholder="Enter Reference"
                    />
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Account <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      name="account_id"
                      value={inputs.account_id || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Account</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg">
                    <label>Type <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      name="type"
                      value={inputs.type || "income"}
                      onChange={handleChange}
                    >
                      <option value="income">Income</option>
                      <option value="expense">Expense</option>
                    </select>
                  </div>
                </div>

                {/* Bank Balance Display */}
                {inputs.account_id && (
                  <div className="row mt-2">
                    <div className="col-lg-6">
                      <div className="alert alert-info" style={{ marginBottom: '15px' }}>
                        <strong>Account Balance:</strong>
                        {loadingBalance ? (
                          <div className="d-flex align-items-center mt-2">
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="sr-only">Loading...</span>
                            </div>
                            <span>Loading balance...</span>
                          </div>
                        ) : balanceError ? (
                          <div className="text-danger mt-2">
                            <i className="fa fa-exclamation-triangle me-1"></i>
                            {balanceError}
                          </div>
                        ) : bankBalance ? (
                          <div className="mt-2">
                            <div className="row">
                              <div className="col-md-4">
                                <small className="text-muted">Bank Name:</small>
                                <div className="font-weight-bold">{bankBalance.name}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Account Number:</small>
                                <div className="font-weight-bold">{bankBalance.account_number}</div>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted">Available Balance:</small>
                                <div className="font-weight-bold text-info">
                                  ৳ {bankBalance.total_balance}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mt-3">
                  <div className="col-lg">
                    <label>Amount <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className="form-control"
                      name="amount"
                      value={inputs.amount || ""}
                      onChange={handleChange}
                      placeholder="Enter Amount"
                    />
                  </div>
                  <div className="col-lg">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={inputs.description || ""}
                      onChange={handleChange}
                      placeholder="Enter Description"
                      rows="3"
                    ></textarea>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-3">
                    <button
                      className="btn btn-primary btn-block mg-b-10"
                      onClick={formSubmit}
                    >
                      <i className="fa fa-send mg-r-10"></i> Update
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionEdit;